import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { GroupDto, UserPublic } from '@mondy/shared';
import { ApiError } from '@mondy/shared/api-client';
import { api } from '@/api';
import { setToken } from '@/lib/auth';
import { useRefreshCurrentUser } from '@/hooks/useCurrentUser';
import { useSetActiveGroup } from '@/state/activeGroup';
import { JoinedGroupModal } from '@/components/JoinedGroupModal';
import {
  ButtonSpinner,
  FormField,
  inputClasses,
  primaryButtonClasses,
} from '@/components/auth/FormField';

type Status =
  | { kind: 'missing' }
  | { kind: 'pending' }
  | {
      kind: 'success';
      joinedGroup: GroupDto | null;
      inviteError: string | null;
    }
  | { kind: 'error'; code: string | null; message: string };

interface VerifyEmailResponse {
  user: UserPublic;
  token: string;
  // Populated when the user signed up via an invite link and the token is
  // still valid at verification time; otherwise null.
  joinedGroup: GroupDto | null;
  // One of 'EXPIRED' / 'EMAIL_MISMATCH' / 'INVALID' / null. Non-fatal —
  // verification still completes.
  inviteError: string | null;
}

function humanizeInviteError(code: string): string {
  switch (code) {
    case 'EXPIRED':
      return 'The invitation has expired.';
    case 'EMAIL_MISMATCH':
      return 'The invitation was sent to a different email address.';
    case 'INVALID':
      return 'The invitation is no longer valid.';
    default:
      return 'The invitation could not be applied.';
  }
}

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const rawNext = searchParams.get('next');
  const next =
    rawNext &&
    (rawNext.startsWith('/accept_group_invite/') ||
      rawNext.startsWith('/join/'))
      ? rawNext
      : null;
  const navigate = useNavigate();
  const refreshMe = useRefreshCurrentUser();
  const setActiveGroupId = useSetActiveGroup();
  const [status, setStatus] = useState<Status>(
    token ? { kind: 'pending' } : { kind: 'missing' },
  );
  const [resendEmail, setResendEmail] = useState('');
  const [resendPending, setResendPending] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [modalDismissed, setModalDismissed] = useState(false);
  const startedRef = useRef(false);

  // Stash the callbacks in refs so the verify effect can depend on `token`
  // alone. `useRefreshCurrentUser` returns a fresh arrow every render, and
  // including it in deps caused the effect to re-run mid-POST — its cleanup
  // ran while the request was in flight, which was fine on its own, but the
  // StrictMode startedRef guard then blocked the follow-up run from finishing
  // the work (token consumed server-side, UI stuck on "Verifying…").
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const refreshMeRef = useRef(refreshMe);
  refreshMeRef.current = refreshMe;
  const setActiveGroupRef = useRef(setActiveGroupId);
  setActiveGroupRef.current = setActiveGroupId;

  useEffect(() => {
    if (!token) return;
    // Guard against React StrictMode double-invocation in dev: single-use
    // tokens would always fail the second call with TOKEN_INVALID.
    if (startedRef.current) return;
    startedRef.current = true;

    (async () => {
      try {
        const res = await api.post<VerifyEmailResponse>(
          '/api/auth/verify-email',
          { token },
        );
        setToken(res.token);
        await refreshMeRef.current();
        if (res.joinedGroup) {
          setActiveGroupRef.current(res.joinedGroup.id);
        }
        setStatus({
          kind: 'success',
          joinedGroup: res.joinedGroup,
          inviteError: res.inviteError,
        });
        // If the server didn't resolve a group join, move on immediately.
        // Otherwise the modal drives navigation on dismiss.
        if (!res.joinedGroup) {
          navigateRef.current(next ?? '/');
        }
      } catch (err) {
        if (err instanceof ApiError) {
          setStatus({ kind: 'error', code: err.code, message: err.message });
        } else {
          setStatus({
            kind: 'error',
            code: null,
            message: 'Verification failed. Please try again.',
          });
        }
      }
    })();
  }, [token, next]);

  async function onResend(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!resendEmail) return;
    setResendPending(true);
    try {
      await api.post<{ ok: true }>('/api/auth/resend-verification', {
        email: resendEmail,
      });
    } catch {
      /* non-enumerating endpoint always returns 200 — ignore */
    } finally {
      setResendPending(false);
      setResendDone(true);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col px-6 py-8">
      <div className="flex flex-1 flex-col justify-center gap-8">
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Verify email</h1>
        </header>

        {status.kind === 'missing' ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-[color:var(--color-fg-muted)]">
              Missing verification token.
            </p>
            <Link
              to="/login"
              className="text-sm text-[color:var(--color-fg)] underline underline-offset-2"
            >
              Back to login
            </Link>
          </div>
        ) : null}

        {status.kind === 'pending' ? (
          <div className="flex items-center gap-3 text-sm text-[color:var(--color-fg-muted)]">
            <ButtonSpinner />
            <span>Verifying your email…</span>
          </div>
        ) : null}

        {status.kind === 'success' ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-[color:var(--color-fg-muted)]">
              Verified! Taking you in…
            </p>
            {status.inviteError ? (
              <p className="text-xs text-[color:var(--color-fg-muted)]">
                The invitation could not be applied —{' '}
                {humanizeInviteError(status.inviteError)}
              </p>
            ) : null}
          </div>
        ) : null}

        {status.kind === 'error' ? (
          <div className="flex flex-col gap-6">
            <p className="text-sm text-[color:var(--color-danger)]">{status.message}</p>

            {status.code === 'TOKEN_EXPIRED' ? (
              <form onSubmit={onResend} className="flex flex-col gap-4">
                <FormField label="Email" htmlFor="resendEmail">
                  <input
                    id="resendEmail"
                    type="email"
                    autoComplete="email"
                    required
                    disabled={resendPending || resendDone}
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className={inputClasses}
                  />
                </FormField>
                <button
                  type="submit"
                  disabled={resendPending || resendDone}
                  aria-busy={resendPending}
                  className={primaryButtonClasses}
                >
                  {resendPending ? (
                    <>
                      <ButtonSpinner />
                      <span>Resending…</span>
                    </>
                  ) : (
                    'Resend verification email'
                  )}
                </button>
                {resendDone ? (
                  <p className="text-xs text-[color:var(--color-fg-muted)]">
                    If an account with that email needs verification, we sent a new link.
                  </p>
                ) : null}
              </form>
            ) : null}

            <Link
              to="/login"
              className="text-sm text-[color:var(--color-fg)] underline underline-offset-2"
            >
              Back to login
            </Link>
          </div>
        ) : null}
      </div>

      {status.kind === 'success' && status.joinedGroup && !modalDismissed ? (
        <JoinedGroupModal
          name={status.joinedGroup.name}
          onDismiss={() => {
            setModalDismissed(true);
            navigate(next ?? '/');
          }}
        />
      ) : null}
    </main>
  );
}
